import { Heart, MessageCircle, MoreHorizontal } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { useState } from 'react'
import Avatar from './ui/Avatar'

export interface CommentType {
  id: string
  author: {
    name: string
    username: string
    avatar?: string
    verified?: boolean
  }
  content: string
  createdAt: string
  likes: number
  replies?: CommentType[]
}

interface CommentProps {
  comment: CommentType
  level?: number
  onReply?: (commentId: string) => void
  onLike?: (commentId: string) => void
}

export default function Comment({ comment, level = 0, onReply, onLike }: CommentProps) {
  const [isLiked, setIsLiked] = useState(false)
  const [likes, setLikes] = useState(comment.likes)
  const [showReplies, setShowReplies] = useState(true)

  const timeAgo = formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })
  const hasReplies = comment.replies && comment.replies.length > 0

  const handleLike = () => {
    setIsLiked(!isLiked)
    setLikes(isLiked ? likes - 1 : likes + 1)
    onLike?.(comment.id)
  }

  const handleReply = () => {
    onReply?.(comment.id)
  }

  return (
    <div className={`${level > 0 ? 'ml-12' : ''}`}>
      <div className="flex gap-3 py-3">
        {/* Avatar with connecting line for nested comments */}
        <div className="relative flex flex-col items-center">
          <Avatar
            src={comment.author.avatar}
            alt={comment.author.name}
            size="md"
          />
          {hasReplies && showReplies && (
            <div className="absolute top-12 bottom-0 w-0.5 bg-gray-200" />
          )}
        </div>

        {/* Comment content */}
        <div className="flex-1 min-w-0">
          {/* Author info */}
          <div className="flex items-center gap-2 mb-1">
            <span className="font-bold text-sm hover:underline cursor-pointer">
              {comment.author.name}
            </span>
            {comment.author.verified && (
              <span className="text-onrecord-orange text-xs">✓</span>
            )}
            <span className="text-gray-500 text-sm">@{comment.author.username}</span>
            <span className="text-gray-500 text-sm">·</span>
            <span className="text-gray-500 text-sm">{timeAgo}</span>
            <button className="ml-auto text-gray-500 hover:text-onrecord-orange p-1 rounded-full hover:bg-orange-50">
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </div>

          {/* Comment text */}
          <p className="text-gray-900 text-sm whitespace-pre-wrap mb-2">{comment.content}</p>

          {/* Action buttons */}
          <div className="flex items-center gap-6">
            <button
              onClick={handleReply}
              className="flex items-center gap-1 text-gray-500 hover:text-onrecord-orange group"
            >
              <div className="group-hover:bg-orange-50 rounded-full p-1.5 transition-colors">
                <MessageCircle className="w-4 h-4" />
              </div>
              {hasReplies && (
                <span className="text-xs">{comment.replies?.length}</span>
              )}
            </button>

            <button
              onClick={handleLike}
              className={`flex items-center gap-1 group ${
                isLiked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
              }`}
            >
              <div className={`rounded-full p-1.5 transition-colors ${
                isLiked ? 'bg-red-50' : 'group-hover:bg-red-50'
              }`}>
                <Heart className={`w-4 h-4 ${isLiked ? 'fill-red-500' : ''}`} />
              </div>
              {likes > 0 && (
                <span className="text-xs">{likes}</span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Nested replies */}
      {hasReplies && showReplies && (
        <div>
          {comment.replies?.map((reply) => (
            <Comment
              key={reply.id}
              comment={reply}
              level={level + 1}
              onReply={onReply}
              onLike={onLike}
            />
          ))}
        </div>
      )}
    </div>
  )
}
